import React, { useEffect, useState } from 'react';
import { BookOpen, Menu, X } from 'lucide-react';

const groups = [
  { title: 'Start building', ids: ['getting-started', 'authentication', 'api-response-format'] },
  { title: 'Products', ids: ['virtual-account-api', 'payout-api', 'merchant-portal-settings', 'card-api', 'sms-api'] },
  { title: 'Integration essentials', ids: ['webhooks', 'error-handling', 'best-practices'] },
  { title: 'Resources', ids: ['support', 'changelog'] },
];
export default function DocumentationLayout({ children, articles, activeId, onSelect, toc, portalLink }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    let frame;
    const updateSection = () => {
      const headings = toc.map(item => document.getElementById(item.id)).filter(Boolean);
      let current = headings[0]?.id || '';
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= 120) current = heading.id;
      }
      // The last heading may never reach the header on short final sections.
      if (window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
        current = headings[headings.length - 1]?.id || '';
      }
      setActiveSection(current);
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateSection);
    };
    updateSection();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [toc, activeId]);
  return <div className="min-h-screen bg-white text-slate-900">
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between gap-4 px-5 lg:px-10">
        <a href="#getting-started" className="flex items-center gap-3 font-bold"><BookOpen className="text-primary" size={26} /><span>WayaBank <span className="ml-2 hidden border-l border-slate-200 pl-3 text-sm font-normal text-slate-500 sm:inline">Developers</span></span></a>
        <div className="flex items-center gap-3">{portalLink}<button aria-label={menuOpen ? 'Close documentation navigation' : 'Open documentation navigation'} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)} className="rounded-lg p-2 lg:hidden">{menuOpen ? <X /> : <Menu />}</button></div>
      </div>
    </header>
    <div className="mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)_200px] lg:gap-10 lg:px-10">
      <aside className={`${menuOpen ? 'block' : 'hidden'} border-b border-slate-200 p-5 lg:sticky lg:top-20 lg:block lg:h-[calc(100vh-5rem)] lg:overflow-y-auto no-scrollbar lg:border-b-0 lg:py-10 lg:pl-0`}>
        <nav aria-label="Documentation articles">{groups.map(group => <div className="mb-8" key={group.title}><p className="mb-3 px-3 text-xs font-bold uppercase tracking-wider text-slate-500">{group.title}</p><ul className="space-y-1">{articles.filter(article => group.ids.includes(article.id)).map(article => <li key={article.id}><a href={`#${article.id}`} aria-current={article.id === activeId ? 'page' : undefined} onClick={event => { event.preventDefault(); onSelect(article.id); setMenuOpen(false); }} className={`block rounded-lg px-3 py-2 text-sm leading-6 ${article.id === activeId ? 'bg-orange-50 font-semibold text-primary' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'}`}>{article.title}</a></li>)}</ul></div>)}</nav>
      </aside>
      <main className="min-w-0 px-5 py-10 lg:px-0 lg:py-12">{children}</main>
      <aside className="sticky top-20 hidden h-[calc(100vh-5rem)] overflow-y-auto py-12 xl:block"><nav aria-label="On this page"><p className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">On this page</p><ul className="space-y-3 border-l border-slate-200">{toc.map(item => <li key={item.id}><a href={`#${item.id}`} aria-current={activeSection === item.id ? 'location' : undefined} className={`-ml-px block border-l-2 pl-4 text-sm leading-6 transition-colors hover:text-primary ${activeSection === item.id ? 'border-primary font-semibold text-primary' : 'border-transparent text-slate-500'}`}>{item.text}</a></li>)}</ul></nav></aside>
    </div>
  </div>;
}
